import type { Attribute } from '../sdk/attributes.ts'
import { resolveAttribute } from '../sdk/attributes.ts'
import { columnIndex, num, type Table } from '../sdk/search.ts'

/**
 * The flow-volume attributes this report needs, resolved by name every time.
 *
 * Ids are cloud-side and get renumbered, so these are documentation, not
 * constants — a wrong resolution is recognisable against them:
 *   14 globalEmitted_dL · 17 florulIexpVol_dl · 18 florulImeasVol_dl
 *   77 florulIunexpVol_dl
 */
export const FLOW_ATTRS = {
  globalExpected: { name: 'globalEmitted_dL', desc: 'Total expected volume', knownId: '14' },
  ruleExpected: { name: 'florulIexpVol_dl', desc: 'Expected volume by flow rule', knownId: '17' },
  ruleMeasured: { name: 'florulImeasVol_dl', desc: 'Measured volume by flow rule', knownId: '18' },
  ruleUnexpected: { name: 'florulIunexpVol_dl', desc: 'Unexpected volume by flow rule', knownId: '77' },
} as const

export type FlowAttrKey = keyof typeof FLOW_ATTRS

export interface ResolvedFlowAttrs {
  resolved: Partial<Record<FlowAttrKey, Attribute>>
  /** Keys the cloud does not offer this account, or does not collect. */
  missing: FlowAttrKey[]
  unavailable: FlowAttrKey[]
  /** Ids that no longer match the documented ones — worth surfacing. */
  renumbered: { key: FlowAttrKey; expected: string; actual: string }[]
}

export function resolveFlowAttrs(attrs: Attribute[]): ResolvedFlowAttrs {
  const resolved: Partial<Record<FlowAttrKey, Attribute>> = {}
  const missing: FlowAttrKey[] = []
  const unavailable: FlowAttrKey[] = []
  const renumbered: { key: FlowAttrKey; expected: string; actual: string }[] = []

  for (const key of Object.keys(FLOW_ATTRS) as FlowAttrKey[]) {
    const want = FLOW_ATTRS[key]
    // Machine name first here: these are fixed cloud identifiers, while the
    // display names are hand-typed upstream and drift.
    const hit = resolveAttribute(attrs, { name: want.name, desc: want.desc }, 'name')
    if (!hit) {
      missing.push(key)
      continue
    }
    resolved[key] = hit
    if (!hit.available) unavailable.push(key)
    if (hit.searchId !== want.knownId) {
      renumbered.push({ key, expected: want.knownId, actual: hit.searchId })
    }
  }
  return { resolved, missing, unavailable, renumbered }
}

/** dL → L. Every flow-volume attribute in group 2 is decilitres. */
export const DL_TO_L = 0.1

export interface FlowVariant {
  id: 'rule' | 'global' | 'reconcile'
  title: string
  blurb: string
  /** Series, in categorical-slot order. */
  series: { key: FlowAttrKey; label: string }[]
}

/**
 * Three framings of the same question, rendered side by side so the difference
 * between them is visible rather than assumed.
 */
export const FLOW_VARIANTS: FlowVariant[] = [
  {
    id: 'rule',
    title: 'Flow rule: expected vs unexpected',
    blurb:
      'Both series scoped to flow rules (filter floRuleId), same units, directly comparable. The apples-to-apples pair.',
    series: [
      { key: 'ruleExpected', label: 'Expected' },
      { key: 'ruleUnexpected', label: 'Unexpected' },
    ],
  },
  {
    id: 'global',
    title: 'Controller total vs unexpected',
    blurb:
      'Expected taken from the whole-controller total, unexpected summed over flow rules. Wider expected coverage — the two are not scoped identically.',
    series: [
      { key: 'globalExpected', label: 'Expected (controller)' },
      { key: 'ruleUnexpected', label: 'Unexpected' },
    ],
  },
  {
    id: 'reconcile',
    title: 'Reconciliation: expected · measured · unexpected',
    blurb:
      'Measured should sit near expected + unexpected. Where it does not, the flow rules are not accounting for everything the sensors saw.',
    series: [
      { key: 'ruleExpected', label: 'Expected' },
      { key: 'ruleMeasured', label: 'Measured' },
      { key: 'ruleUnexpected', label: 'Unexpected' },
    ],
  },
]

/**
 * A search reply is the whole record, not one column — a fetch for expected
 * volume can already carry measured and unexpected. Reusing it saves fetching
 * the same megabytes several times, but only when the column is genuinely
 * populated, so an all-empty column never masquerades as data.
 */
export function tableCovers(table: Table, machineName: string): boolean {
  const idx = columnIndex(table, machineName)
  if (idx < 0) return false
  for (const row of table.rows) {
    if (num(row[idx]) !== null) return true
  }
  return false
}
